import { useState, useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { LegacySpinner } from '@/ui/LegacySpinner';
import { useClientStore } from '@/client/client-store';
import { resolveLegacyRouteTarget } from '@/router/legacy-route-adapter';
import { openPopupMfe } from '@/mfe/popup-store';
import { pushToast } from '@/ui/toast-store';
import { useAppTiles, RAW_TILES_QUERY_KEY, type AppTileView } from '@/apps/use-app-tiles';
import { queryClient } from '@/http/query-client';
import { MotifButton } from '@ey-xd/motif-wc-react';

interface AppsPanelProps {
  onClose: () => void;
}

/** Tile icon: real CDN image, falling back to the Material `apps` glyph. */
function PanelTileIcon({ tile }: { tile: AppTileView }) {
  const [broken, setBroken] = useState(false);
  if (!tile.iconUrl || broken) {
    return (
      <span className="material-icons-outlined" aria-hidden="true">
        apps
      </span>
    );
  }
  return <img src={tile.iconUrl} alt="" loading="lazy" onError={() => setBroken(true)} />;
}

export function AppsPanel({ onClose }: AppsPanelProps) {
  const router = useRouter();
  const currentClient = useClientStore((s) => s.current);
  const { data: tiles, isLoading, isError, refetch } = useAppTiles(currentClient?.id);

  // Refetch tiles every time the panel opens (matching Angular's ngOnInit
  // which calls loadMyApps on every open). This ensures the favorite state
  // is always fresh from the backend, reflecting changes made in the home
  // page or workspace MFE.
  useEffect(() => {
    // Drop the shared raw-tiles cache so the refetch re-hits GetTiles for fresh favorites.
    if (currentClient?.id) {
      void queryClient.invalidateQueries({ queryKey: [RAW_TILES_QUERY_KEY, currentClient.id] });
    }
    void refetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openApp = (tile: AppTileView) => {
    onClose();
    if (tile.isExternal) {
      if (tile.target) window.open(tile.target, '_blank', 'noopener,noreferrer');
      return;
    }
    const resolution = resolveLegacyRouteTarget(tile.target);
    if (!resolution.supported) {
      pushToast({
        variant: 'info',
        message: resolution.reason ?? `${tile.name} is not available.`,
      });
      return;
    }
    if (resolution.kind === 'popup') {
      openPopupMfe(resolution.appId);
      return;
    }
    if (resolution.kind === 'external') {
      window.open(resolution.url, '_blank', 'noopener,noreferrer');
      return;
    }
    void router.navigate({ to: resolution.path });
  };

  if (isLoading) {
    return (
      <div className="header-utility-panel__status" role="status">
        <LegacySpinner />
        <span>Loading apps&hellip;</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="header-utility-panel__status" role="alert">
        <p>Couldn&apos;t load your apps.</p>
        <MotifButton variant="secondary" size="sm" type="button" onClick={() => void refetch()}>
          Try again
        </MotifButton>
      </div>
    );
  }

  if (!tiles || tiles.length === 0) {
    return (
      <div className="header-utility-panel__status" role="status">
        <p>No apps available for this client.</p>
      </div>
    );
  }

  const favoriteApps = tiles.filter((t) => t.isFavorite);
  const otherApps = tiles.filter((t) => !t.isFavorite);

  const renderRow = (tile: AppTileView) => (
    <div className="header-utility-panel__app-row" key={tile.id} role="listitem">
      <button
        type="button"
        className="header-utility-panel__app-open"
        title={tile.isExternal ? `Redirect to ${tile.name}` : `Open ${tile.name}`}
        onClick={() => openApp(tile)}
      >
        <PanelTileIcon tile={tile} />
        <span>
          <strong>{tile.name}</strong>
        </span>
      </button>
    </div>
  );

  return (
    <div className="header-utility-panel__apps" role="list">
      {favoriteApps.length > 0 && (
        <>
          <div className="header-utility-panel__apps-section-label">Favorite Apps</div>
          {favoriteApps.map(renderRow)}
        </>
      )}
      {otherApps.length > 0 && (
        <>
          <div className="header-utility-panel__apps-section-label">My Apps</div>
          {otherApps.map(renderRow)}
        </>
      )}
    </div>
  );
}
